import { Box, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isCommissionProduct } from '@vy/services';
import type { PlanInputFormFixedProduct } from '@vy/types';
import { isNotNullOrUndefined, withDefault } from '@vy/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { getDefaultCommissionProduct } from '../../../../../create-plan-page-consts/add-item-button-consts';
import { useGetDefaultProductConfigurations } from '../../../../../create-plan-page-hooks/useGetDefaultProductConfigurations';
import {
  PRO_RATA_SETTINGS,
  mapProRataSettingToProductFields,
} from '../../../../CreatePlanForm/create-plan-form-types/create-plan-form-sub-types';
import type { NewPlanForm } from '../../../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../../../CreatePlanForm/create-plan-form-types/plan-input-form';

type FixedChargesMenuItemsProps = {
  onAddCreditClick?: () => void;
};

export const FixedChargesMenuItems: React.FC<FixedChargesMenuItemsProps> = ({ onAddCreditClick }) => {
  const { setValue, getValues } = useFormContext<PlanInputForm, NewPlanForm>();
  const {
    getFixedOneTimeProductConfiguration,
    getCommercialTermsProductConfiguration,
    getSubscriptionProductConfiguration,
  } = useGetDefaultProductConfigurations();

  const fixedProducts = withDefault(getValues('fixedProducts'), []);
  const commissionProducts = fixedProducts.filter(isCommissionProduct);
  const doesPlanHaveCommission = commissionProducts.length > 0;

  const handleProductClick = (
    defaultProductCallback: () => PlanInputFormFixedProduct,
    shouldAddProRataSettings = false,
  ) => {
    return () => {
      const currentProducts = getValues('fixedProducts') ?? [];
      const defaultProduct = defaultProductCallback();
      const proRataSetting = getValues('proRataSetting') ?? PRO_RATA_SETTINGS.PRO_RATA_AND_CALENDAR_ALIGNED;
      const { proRata, isCalendarAligned } = mapProRataSettingToProductFields(proRataSetting);
      const productWithProRataSettings = shouldAddProRataSettings
        ? { ...defaultProduct, proRata, isCalendarAligned }
        : defaultProduct;

      const updatedProducts = [...currentProducts, productWithProRataSettings];
      setValue('fixedProducts', updatedProducts);
    };
  };

  return (
    <>
      <MenuItem
        className="flex flex-col items-start"
        onClick={handleProductClick(getFixedOneTimeProductConfiguration)}
        {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.fixedCharges.oneTimeFee)}
      >
        <Typography className="text-sm font-normal text-black">One Time Fee</Typography>
        <Typography className="text-xs font-normal text-[#94A0A4]">Single one time charge</Typography>
      </MenuItem>

      <MenuItem
        className="flex flex-col items-start"
        onClick={handleProductClick(getSubscriptionProductConfiguration, true)}
        {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.fixedCharges.subscriptionFee)}
      >
        <Typography className="text-sm font-normal text-black">Subscription</Typography>
        <Typography className="text-xs font-normal text-[#94A0A4]">Fixed monthly amount</Typography>
      </MenuItem>

      <MenuItem
        className="flex flex-col items-start"
        onClick={handleProductClick(getCommercialTermsProductConfiguration)}
      >
        <Typography className="text-sm font-normal text-black">Contract Terms</Typography>
        <Typography className="text-xs font-normal text-[#94A0A4]">Contract commitment</Typography>
      </MenuItem>

      <MenuItem
        className="flex flex-col items-start"
        onClick={handleProductClick(getDefaultCommissionProduct)}
        disabled={doesPlanHaveCommission}
      >
        <Typography className="text-sm font-normal text-black">Commission</Typography>
        <Typography className="text-xs font-normal text-[#94A0A4]">Percentage from total amount</Typography>
      </MenuItem>

      {isNotNullOrUndefined(onAddCreditClick) && (
        <MenuItem
          className="flex flex-col items-start"
          onClick={onAddCreditClick}
          {...getSelectorAttribute(createPlanPageSelectors.tabs.sections.fixedCharges.creditFee)}
        >
          <Box className="flex items-center gap-x-1">
            <Typography className="text-sm font-normal text-black">Credit</Typography>
            <Box
              sx={{
                borderRadius: '4px',
                bgcolor: '#E7F2FF',
                color: '#0521FF',
                fontSize: '10px',
                fontWeight: 600,
                lineHeight: 1,
                padding: '2px 4px',
              }}
            >
              New
            </Box>
          </Box>
          <Typography className="text-xs font-normal text-[#94A0A4]">
            Prepaid balance for future charges
          </Typography>
        </MenuItem>
      )}
    </>
  );
};
